import { API_CONFIG } from '@/config/api';
import { http } from '../http';

export interface ApiResponse<T = unknown> {
  res_status_code: string;
  res_message: string;
  res_content: T;
  exception_message?: string | null;
}

export interface SearchToolFieldConf {
  id: number;
  function_method: string;
  function_name: string | null;
  create_time: string;
  create_user_id: number;
  update_user_id: number;
  update_time: string;
  default_field_list: unknown[] | null;
  field_list: unknown[];
}

export interface SearchToolContent<T> {
  field_conf: SearchToolFieldConf;
  total_pages: number;
  page_index: number;
  total_count: number;
  data_list: T[];
  sum_data: unknown[] | null;
  columns: unknown[] | null;
  group_name: string | null;
  tips: string | null;
  specific_field_result: unknown | null;
  exception_message: string | null;
}

export type GroupBy = 'brand' | 'category' | 'spu' | 'sku';
export type MonthViewMode = 'year' | 'rolling';

export interface SpuAmountFilterItem {
  brand_name: string | null;
  catagory_name_lv3: string | null;
  relation_spu_no: string | null;
  relation_spu_name: string | null;
  goods_no?: string | null;
  goods_name?: string | null;
}

export interface SpuAmountReportRow {
  total_groups: number;
  rn: number;
  group_id: string;
  brand_code: string | null;
  brand_name: string | null;
  catagory_name_lv3: string | null;
  relation_spu: string | null;
  relation_spu_no: string | null;
  relation_spu_name: string | null;
  spu_no: string | null;
  spu_name: string | null;
  goods_no: string | null;
  goods_name: string | null;
  goods_property?: string | null;
  sku_abnormal_flag?: number | null;
  budget_peak_abs_diff_rate?: number | null;
  sku_peak_abs_deviation?: number | null;
  refresh_time?: string | null;
  sort_no: number;
  metric_name: string;
  m01: number | null;
  m02: number | null;
  m03: number | null;
  m04: number | null;
  m05: number | null;
  m06: number | null;
  m07: number | null;
  m08: number | null;
  m09: number | null;
  m10: number | null;
  m11: number | null;
  m12: number | null;
  w1: number | null;
  w2: number | null;
  w3: number | null;
  w4: number | null;
  w5: number | null;
  w6: number | null;
  w7: number | null;
  ttl_amt: number | null;
  ytd_amt: number | null;
  ttl_share: number | null;
  ytd_share: number | null;
}

export interface SpuAmountQuery {
  bizDate: string;
  groupPageNo: number;
  groupPageSize: number;
  groupLevel: GroupBy;
  monthViewMode?: MonthViewMode;
  brandName?: string;
  categoryNameLv3?: string;
  spuNo?: string;
  goodsName?: string;
  budgetAbnormal?: 'all' | 'abnormal' | 'normal';
  skuAbnormal?: 'all' | 'abnormal' | 'normal';
  productAttrName?: string;
  specName?: string;
}

const REPORT_SEARCH_ID = 1139;
const SKU_REPORT_SEARCH_ID = 1141;
const ROLLING_REPORT_SEARCH_ID = REPORT_SEARCH_ID;
const ROLLING_SKU_REPORT_SEARCH_ID = SKU_REPORT_SEARCH_ID;
const FILTER_SEARCH_ID = 1130;

const getReportSearchId = (params: SpuAmountQuery) => {
  if (params.monthViewMode === 'rolling') {
    const searchId = params.groupLevel === 'sku' ? ROLLING_SKU_REPORT_SEARCH_ID : ROLLING_REPORT_SEARCH_ID;
    if (!searchId) {
      throw new Error('滚动月份口径的 search_id 尚未配置');
    }
    return searchId;
  }

  return params.groupLevel === 'sku' ? SKU_REPORT_SEARCH_ID : REPORT_SEARCH_ID;
};

export const spuAmountApi = {
  getReport: async (params: SpuAmountQuery): Promise<ApiResponse<SearchToolContent<SpuAmountReportRow>>> => {
    const searchId = getReportSearchId(params);
    return http.get<ApiResponse<SearchToolContent<SpuAmountReportRow>>>(API_CONFIG.endpoints.searchTool, {
      params: {
        page_size: Math.max(200, params.groupPageSize * 10),
        page_no: 1,
        search_id: searchId,
        '@biz_date': params.bizDate,
        '@group_page_no': params.groupPageNo,
        '@group_page_size': params.groupPageSize,
        '@group_level': params.groupLevel,
        '@month_view_mode': params.monthViewMode ?? 'year',
        '@brand_name': params.brandName ?? '',
        '@catagory_name_lv3': params.categoryNameLv3 ?? '',
        '@spu_no': params.spuNo ?? '',
        '@goods_name': params.goodsName ?? '',
        '@budget_abnormal': params.budgetAbnormal ?? 'all',
        '@sku_abnormal': params.skuAbnormal ?? 'all',
        '@product_attr_name': params.productAttrName ?? '',
        '@spec_name': params.specName ?? '',
      },
    });
  },

  getFilterOptions: async (brandName?: string, categoryNameLv3?: string): Promise<ApiResponse<SearchToolContent<SpuAmountFilterItem>>> =>
    http.get<ApiResponse<SearchToolContent<SpuAmountFilterItem>>>(API_CONFIG.endpoints.searchTool, {
      params: {
        page_size: 5000,
        page_no: 1,
        search_id: FILTER_SEARCH_ID,
        '@brand_name': brandName ?? '',
        '@catagory_name_lv3': categoryNameLv3 ?? '',
      },
    }),
};
